import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import CheckboxField from './Fields/CheckboxField'
import {
  priceFieldProps,
  priceRegisterConfig,
  required,
  urlRegisterConfig,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import { CreateActivityItemViewModel } from '../Create/models'
import { UpdateActivityItemViewModel } from '../Edit/models'

import Hr from 'components/Common/Hr'
import InlineGroup from 'components/Common/InlineGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'components/Luxkit/Typography/Heading'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import AddressAutocomplete, {
  AddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/AddressAutocomplete'
import TimeInput from 'tripPlanner/components/TripModal/TripItems/Common/Fields/TimeInput'
import { useComponentRestrictions } from 'tripPlanner/hooks/Google/useComponentRestrictions'
import {
  ADDRESS_RESPONSE_DETAILS,
  AutocompletePlaceParam,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []
const START_DEP_KEYS: string[] = ['startPlace.name']
const END_DEP_KEYS: string[] = ['endPlace.name']

const InputRow = styled(InlineGroup)`
  margin-bottom: ${rem(24)};
`
interface Props {
  tripId: string
}

const ActivityFields = ({ tripId }: Props) => {
  const methods = useFormContext<
    CreateActivityItemViewModel | UpdateActivityItemViewModel
  >()
  const { watch, control, setValue, register } = methods

  useEnsureGeoPlace(methods, 'startPlace', START_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_DEP_KEYS)
  const componentRestrictions = useComponentRestrictions(tripId)

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const isEndDateSame = watch('isEndDateSame')
  const isEndPlaceSame = watch('isEndPlaceSame')
  const watchBooked = watch('isBooked')
  const startPlace = watch('startPlace')
  const startDate = watch('startDate')
  const endPlace = watch('endPlace')

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: AddressAutocompleteOption,
    ) => {
      setValue(
        `${placeName}.name`,
        selectedOption.details?.name || selectedOption.label,
      )

      setValue(
        `${placeName}.type`,
        selectedOption.prediction ? 'GEO' : 'CUSTOM',
      )

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction // eslint-disable-line @typescript-eslint/naming-convention
        setValue(`${placeName}.id`, place_id!)
      }

      if (selectedOption.details) {
        const {
          geometry,
          formatted_address, // eslint-disable-line @typescript-eslint/naming-convention
          website,
          address_components, // eslint-disable-line @typescript-eslint/naming-convention
        } = selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        if (website) {
          setValue('url', website)
        }
        formatted_address && setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat())
          setValue(`${placeName}.point.lng`, geometry.location.lng())
        }
      }
      if (placeName === 'startPlace' && isEndPlaceSame) {
        handleAddressChange('endPlace', selectedOption)
      }
    },
    [setValue, isEndPlaceSame],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: AddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: AddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleSamePlaceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue('isEndPlaceSame', e.currentTarget.checked)

      if (e.currentTarget.checked && 'id' in startPlace && startPlace.id) {
        setValue('endPlace', startPlace)
      }
    },
    [setValue, startPlace],
  )

  const handleSameDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue('isEndDateSame', e.currentTarget.checked)

      if (e.currentTarget.checked && startDate) {
        setValue('endDate', startDate)
      }
    },
    [setValue, startDate],
  )

  const handleStartDateChange = useCallback(
    (date?: Date) => {
      if (isEndDateSame) {
        setValue('endDate', moment(date).format(ISO_DATE_FORMAT))
      }
    },
    [isEndDateSame, setValue],
  )

  return (
    <VerticalSpacer gap={8}>
      <Input
        {...register('name', { required })}
        label="Activity name"
        placeholder="Activity name"
        required
      />
      <Heading variant="heading6">Start</Heading>
      <AddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label="Location"
        placeholder="Search by landmark or address"
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        autocompletionReqParam={{
          componentRestrictions,
        }}
      />
      {startPlace.address && (
        <AddressDisplay title="Address" value={startPlace.address} />
      )}
      <InputRow equal horizontal="between" spacing="lg">
        {/* @ts-ignore default values for props of class component */}
        <RHFDatePickerInput
          name="startDate"
          label="Date"
          noValidationSpacing
          onDateChange={handleStartDateChange}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label="Time"
          placeholder="Select time"
          noValidationSpacing
        />
      </InputRow>
      <Hr size="lg" />
      <Heading variant="heading6">End</Heading>
      <Controller
        name="isEndPlaceSame"
        control={control}
        render={() => (
          <CheckboxField
            defaultChecked={isEndPlaceSame}
            onChange={handleSamePlaceChange}
            label="End location same as start"
          />
        )}
      />
      <AddressAutocomplete
        {...register('endPlace.name', { required })}
        required
        label="Location"
        placeholder="Search by landmark or address"
        initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleEndAddressChange}
        disabled={isEndPlaceSame}
      />
      {endPlace.address && (
        <AddressDisplay title="Address" value={endPlace.address} />
      )}

      <Controller
        name="isEndDateSame"
        control={control}
        render={() => (
          <CheckboxField
            defaultChecked={isEndDateSame}
            onChange={handleSameDateChange}
            label="End date same as start"
          />
        )}
      />

      <InputRow equal horizontal="between" spacing="lg">
        {/* @ts-ignore default values for props of class component */}
        <RHFDatePickerInput
          disabled={isEndDateSame}
          name="endDate"
          label="Date"
          noValidationSpacing
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label="Time"
          placeholder="00:00 am"
          noValidationSpacing
        />
      </InputRow>

      <Hr size="lg" />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      {watchBooked && (
        <Input
          {...register('confirmationCode')}
          label="Confirmation number"
          placeholder="Confirmation number"
        />
      )}

      <Input
        {...register('price', { ...priceRegisterConfig })}
        label="Price"
        placeholder="Price"
        {...priceFieldProps}
      />

      <Input
        {...register('url', { ...urlRegisterConfig })}
        label="Website"
        placeholder="Website"
      />
      <TextArea
        {...register('notes')}
        placeholder="Add notes about this booking"
        label="Additional notes"
        resize="vertical"
      />
    </VerticalSpacer>
  )
}

export default ActivityFields
